import { ApiClient } from '../apiClient';
import { CreateOrderRequest, CreateOrderResponse, Order, OrderFeedbackRating, OrderMetadata } from '../../types/order';
import { PaginationDto } from '../../types/pagination';
import { ValidationError } from '../errors';

export class OrderService {
  constructor(private apiClient: ApiClient) {}

  /**
   * Create a new order
   * Validates required fields before submitting to prevent backend errors
   */
  async createOrder(orderData: CreateOrderRequest): Promise<CreateOrderResponse> {
    // Validate required fields
    if (!orderData.email || orderData.email.trim() === '') {
      throw new ValidationError('Email is required');
    }

    if (!orderData.currency || orderData.currency.trim() === '') {
      throw new ValidationError('Currency is required');
    }

    if (!orderData.cartId || orderData.cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    // /gift/buy is not idempotent - it creates an order, debits balance,
    // and may charge via Coingate. A timeout-triggered retry would run
    // through the BE's in-progress lock and surface a 400 to the user
    // while the first call completes successfully. Opt out of the global
    // retry policy, and give the request a longer timeout since the full-
    // balance path runs GIFQ calls synchronously server-side.
    return this.apiClient.post<CreateOrderResponse>(
      '/gift/buy',
      orderData,
      { timeout: 60000, metadata: { skipRetry: true } },
    );
  }

  /**
   * Get order details by ID
   */
  async getOrder(orderId: string): Promise<OrderMetadata> {
    return this.apiClient.get<OrderMetadata>(`/gift/order/${orderId}/metadata`);
  }

  /**
   * Orders placed through the public agent API pay here: the provider session
   * is opened when the shopper arrives (same session on a repeat call).
   */
  async openPayment(orderId: string, turnstileToken?: string): Promise<{ paymentUrl: string }> {
    return this.apiClient.post<{ paymentUrl: string }>(`/gift/order/${orderId}/pay`, { turnstileToken });
  }

  /**
   * Submit post-purchase feedback (thumbs + optional comment). The backend
   * upserts by orderId, so the thumb click and a later comment submit land
   * on the same document.
   */
  async submitOrderFeedback(
    orderId: string,
    rating: OrderFeedbackRating,
    comment?: string,
  ): Promise<boolean> {
    return this.apiClient.post<boolean>(`/gift/order/${orderId}/feedback`, {
      rating,
      ...(comment ? { comment } : {}),
    });
  }

  async getMyOrders(page = 0, limit = 10): Promise<PaginationDto<Order>> {
    return this.apiClient.get<PaginationDto<Order>>('/order/list', { page, limit });
  }

  async getMyOrderById(orderId: string): Promise<Order> {
    return this.apiClient.get<Order>(`/order/${orderId}`);
  }
}